// URL regex for matching links in plain text
const URL_REGEX = /\b(https?:\/\/|www\.)[^\s<>()]+[^\s<.,:;"')\]\s]/g;

/**
 * Extracts all unique links from a raw HTML string using DOM parsing.
 * @param html - The raw HTML string.
 * @returns An array of unique link URLs.
 */
export function extractLinksFromHtmlWithTiptap(html: string): string[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const anchors = doc.querySelectorAll("a[href]");
  const urls = new Set<string>();

  anchors.forEach((a) => {
    const href = a.getAttribute("href");
    if (href) {
      urls.add(href);
    }
  });

  return Array.from(urls);
}

/**
 * Extracts all unique links from plain text (markdown input).
 * @param text - Plain text string (may contain markdown).
 * @returns An array of unique link URLs.
 */
export function extractLinksFromText(text: string): string[] {
  const urls = new Set<string>();
  const matches = text.matchAll(URL_REGEX);

  for (const match of matches) {
    let url = match[0];
    if (url.startsWith("www.")) {
      url = `https://${url}`;
    }
    urls.add(url);
  }

  // Also extract markdown-style links: [text](url)
  const mdLinkRegex = /\[[^\]]*\]\((https?:\/\/[^)]+)\)/g;
  const mdMatches = text.matchAll(mdLinkRegex);
  for (const match of mdMatches) {
    urls.add(match[1]);
  }

  return Array.from(urls);
}

/**
 * Returns true only if the URL has a structurally valid hostname:
 * the TLD (after the last dot) is ≥ 2 characters, and if the hostname
 * starts with "www.", the part after it must also contain a dot —
 * so "www.example.com" is valid but "www.webd" is not.
 */
export function isCompleteUrl(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    const lastDot = hostname.lastIndexOf(".");
    if (lastDot === -1) {
      return false;
    }
    if (hostname.slice(lastDot + 1).length < 2) {
      return false;
    }
    if (hostname.startsWith("www.") && !hostname.slice(4).includes(".")) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Determines whether link extraction should run based on how the text changed.
 *
 * Returns true when:
 * - Text is empty (need to clear link state)
 * - Characters were deleted (lengthDiff < 0) — clears stale preview
 * - Multiple characters added at once (paste / programmatic set)
 * - Exactly one whitespace character was appended (space after URL completes it)
 *
 * Returns false when a single non-whitespace character was added, i.e. the user
 * is still typing a URL one letter at a time — matching TipTap's "space triggers
 * autolink" behaviour.
 */
export function shouldTriggerLinkExtraction(
  prev: string,
  next: string,
): boolean {
  if (next.length === 0) {
    return true;
  }

  const lengthDiff = next.length - prev.length;

  if (lengthDiff < 0) {
    return true;
  } // deletion
  if (lengthDiff > 1) {
    return true;
  } // paste or programmatic set

  // Single character added — only trigger on whitespace (space / newline / tab)
  if (lengthDiff === 1) {
    return /\s/.test(next.slice(-1));
  }

  return false; // no change
}

/**
 * Extracts domain url from a link string.
 * @param url - link string
 * @returns The domain of the link or null if invalid.
 */
export function extractDomainHostname(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.hostname;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
}

export function extractDomainOrigin(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.origin;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
}

export function extractDomainFavicon(url: string): string | null {
  try {
    const domainHostname = extractDomainHostname(url);

    const _favWithDDGO =
      domainHostname &&
      `https://icons.duckduckgo.com/ip3/${domainHostname}.ico`;
    const _favWithNormal = domainHostname && `${domainHostname}/favicon.ico`;

    return _favWithDDGO || _favWithNormal;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return null;
  }
}

export function extractDomainName(url: string): string | null {
  try {
    const parsedUrl = new URL(url);
    let hostname = parsedUrl.hostname;

    if (hostname.startsWith("www.")) {
      hostname = hostname.slice(4);
    }

    return hostname;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    const domainMatch = url.match(/^(?:https?:\/\/)?(?:www\.)?([^/\s'">]+)/i);
    if (domainMatch && domainMatch[1]) {
      return domainMatch[1];
    }
    return null;
  }
}
